import { Dispatch, SetStateAction, useCallback, useContext } from 'react';
import { TSelectedItem, TSelectVersionDialogState, useGetVersionInfo } from '@modules/items';
import { TBlockVersionMode, TVersion } from '../types';
import { TFileVersion } from '@modules/files';
import { ToastContext } from '@shared/contexts';
import { TERMINOLOGY } from '@shared/i18n';
import DocumentContext from '../contexts/DocumentContext.context';
import { TSelectBlockDialogState } from '../types/internal.types';

type TParams = {
  setSelectBlockDialogState: Dispatch<SetStateAction<TSelectBlockDialogState | undefined>>;
  setSelectVersionDialogState: Dispatch<SetStateAction<TSelectVersionDialogState | undefined>>;
};

const useInsertBlock = ({ setSelectBlockDialogState, setSelectVersionDialogState }: TParams) => {
  const { editor } = useContext(DocumentContext);
  const { show } = useContext(ToastContext);
  const getVersionInfo = useGetVersionInfo();

  const insertBlock = (selectedItem: TSelectedItem, versionMode: TBlockVersionMode, versionId?: string) => {
    if (selectedItem.type === 'DOCUMENT')
      editor.chain().focus().insertBlock(selectedItem._id, versionMode, versionId).run();
    else if (selectedItem.type === 'INLINE_BLOCK')
      editor.chain().focus().insertInlineBlock(selectedItem._id, versionMode, versionId).run();
    else {
      show('error', `You can only insert blocks!`);
      console.error(`Cannot insert block for item type: ${selectedItem.type}!`);
    }
  };

  const handleInsertBlock = useCallback(
    (blocksFolderId: string | undefined) => {
      setSelectBlockDialogState({
        isOpen: true,
        blocksFolderId,
        onSelect: async (selectedItems) => {
          const { _id, type, name } = selectedItems[0];
          const versionInfo = await getVersionInfo.mutateAsync({ id: _id, itemType: type });
          if (versionInfo.versions.length < 1) return show('error', `${name} has no completed version!`);
          setSelectBlockDialogState(undefined);

          setSelectVersionDialogState({
            title: 'Select version to reference',
            isOpen: true,
            versionInfo: { ...versionInfo, draft: undefined },
            onSelect: async (versionMode, versionId) => {
              let version: TVersion | TFileVersion | undefined;
              if (versionMode === 'AUTO') version = versionInfo.versions[0];
              else version = versionInfo.versions.find((v) => v.id === versionId);
              if (!version) {
                console.error(
                  `Version not found for ${TERMINOLOGY.blockEither.lower}! (${versionMode}, ${versionId})!`
                );
                return show('error', 'Action failed!');
              }

              insertBlock(selectedItems[0], versionMode as TBlockVersionMode, versionId);
              setSelectVersionDialogState(undefined);
            },
          });
        },
      });
    },
    [insertBlock, getVersionInfo]
  );

  return handleInsertBlock;
};

export default useInsertBlock;
